import { cdn_url, generateThumbnailFromURL, loadImageWithRetry } from '@/utils';
import { Box, CircularProgress, Theme, useMediaQuery } from '@mui/material';
import Image from 'next/image';
import React, { useEffect, useState } from 'react';

interface VideoThumbnailProps {
  item: any;                // can be a Memory or a complementaryVideo entry
  extendedPalette?: any;
}

const withCDN = (key?: string | null) =>
  key ? `${cdn_url}${key.split('/').map(encodeURIComponent).join('/')}` : undefined;

const pickThumbKey = (item: any) =>
  item?.thumb ?? item?.preview_asset ?? item?.memory_details?.preview_asset ?? null;

const pickVideoForFallback = (item: any) => {
  // Priority: explicit mp4 rendition → memory.asset → original filename
  const versions = item?.versions;
  const mp4FromVersions = Array.isArray(versions)
    ? versions.find((v: any) => /\.mp4$/i.test(v?.video || ''))?.video
    : undefined;

  return (
    mp4FromVersions ??
    item?.video ??
    item?.asset ??
    item?.fileName ??
    item?.filename ??
    item?.memory_details?.complementaryVideo?.[0]?.video ??
    item?.memory_details?.complementaryVideo?.[0]?.fileName ??
    null
  );
};

const VideoThumbnail: React.FC<VideoThumbnailProps> = ({ item, extendedPalette }) => {
  const [thumbSrc, setThumbSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const isLarge = useMediaQuery((t: Theme) => t.breakpoints.up('lg'));

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        if (!item) return;

        // 1) If server provided a thumb, try to load it from CDN with retries
        const thumbKey = pickThumbKey(item);
        if (thumbKey) {
          const cdnThumb = withCDN(thumbKey)!;
          const okUrl = await loadImageWithRetry(cdnThumb);
          if (!cancelled && okUrl) {
            setThumbSrc(okUrl);
            return;
          }
        }

        // 2) Fallback: generate a local thumbnail from the best-guess video URL
        const videoKey = pickVideoForFallback(item);
        if (!videoKey) return;

        const absoluteVideoUrl =
          /^https?:\/\//i.test(videoKey) || videoKey.startsWith('data:')
            ? videoKey
            : withCDN(videoKey)!;

        const dataUrl = await generateThumbnailFromURL(absoluteVideoUrl).catch(() => null);
        if (!cancelled && dataUrl) setThumbSrc(dataUrl);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    setLoading(true);
    setThumbSrc(null);
    run();

    return () => {
      cancelled = true;
    };
  }, [item]);

  return (
    <Box minHeight={isLarge ? '14.4rem' : '100%'} position="relative">
      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="10rem">
          <CircularProgress sx={{ color: extendedPalette?.buttonbackgroundIcon }} />
        </Box>
      )}

      {!loading && thumbSrc && (
        <Box sx={{ position: 'relative', width: '100%', minHeight: isLarge ? '14.4rem' : '10rem' }}>
          <Image
            src={thumbSrc}
            alt="Video Thumbnail"
            fill
            sizes="(max-width: 1024px) 100vw, 50vw"
            style={{ objectFit: 'contain' }}
            priority
          />
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              cursor: 'pointer',
              width: 60,
              height: 60,
              backgroundImage: "url('/images/buttonPlay.png')",
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              borderRadius: '50%',
            }}
          />
        </Box>
      )}

      {!loading && !thumbSrc && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="10rem">
          <CircularProgress sx={{ color: extendedPalette?.buttonbackgroundIcon }} />
        </Box>
      )}
    </Box>
  );
};

export default VideoThumbnail;
